// Drag-look + zoom controls. Pointer Events covers mouse, pen, and touch in one path.
// Pinch (two-finger) drives FOV; mouse-wheel does the same on desktop.

import * as pc from 'playcanvas';
import { clamp } from './mathUtils';

const PITCH_LIMIT_DEG = 85;
const FOV_MIN = 35;
const FOV_MAX = 95;
const DRAG_SENSITIVITY = 0.18; // degrees per CSS pixel
const DAMPING = 0.18; // 0 = no smoothing, 1 = no motion

export interface CameraControlsOptions {
  initialYaw?: number;
  initialPitch?: number;
  initialFov?: number;
}

export class CameraControls {
  private readonly canvas: HTMLCanvasElement;
  private readonly camera: pc.Entity;
  private readonly app: pc.Application;

  private targetYaw: number;
  private targetPitch: number;
  private currentYaw: number;
  private currentPitch: number;
  private targetFov: number;
  private currentFov: number;

  private enabled = true;
  private readonly activePointers = new Map<number, { x: number; y: number }>();
  private lastPinchDistance: number | null = null;

  constructor(canvas: HTMLCanvasElement, camera: pc.Entity, app: pc.Application, opts: CameraControlsOptions = {}) {
    this.canvas = canvas;
    this.camera = camera;
    this.app = app;
    this.targetYaw = this.currentYaw = opts.initialYaw ?? 0;
    this.targetPitch = this.currentPitch = opts.initialPitch ?? 0;
    this.targetFov = this.currentFov = opts.initialFov ?? camera.camera?.fov ?? 72;
    this.attach();
    this.app.on('update', this.tick);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.activePointers.clear();
      this.lastPinchDistance = null;
    }
  }

  setLook(yaw: number, pitch: number, opts: { snap?: boolean } = {}): void {
    this.targetYaw = yaw;
    this.targetPitch = clamp(pitch, -PITCH_LIMIT_DEG, PITCH_LIMIT_DEG);
    if (opts.snap) {
      this.currentYaw = this.targetYaw;
      this.currentPitch = this.targetPitch;
      this.applyToCamera();
    }
  }

  getLook(): { yaw: number; pitch: number; fov: number } {
    return { yaw: this.currentYaw, pitch: this.currentPitch, fov: this.currentFov };
  }

  /**
   * External target FOV setter — used by the dolly-toward-hotspot navigation
   * transition (App.dollyTowards) to narrow the FOV while easing the camera
   * toward a door, then snap back to the room's default after the fade.
   * Internal pinch / wheel zooming still works via the existing pointer
   * handlers below; this is just the programmatic entry point.
   */
  setFov(fov: number, opts: { snap?: boolean } = {}): void {
    this.targetFov = fov;
    if (opts.snap) {
      this.currentFov = this.targetFov;
      this.applyToCamera();
    }
  }

  destroy(): void {
    this.detach();
    this.app.off('update', this.tick);
  }

  // ── Internals ───────────────────────────────────────────────

  private readonly tick = (_dt: number): void => {
    // Smooth motion towards target — frame-independent enough for 60-90Hz.
    this.currentYaw = lerpAngle(this.currentYaw, this.targetYaw, 1 - DAMPING);
    this.currentPitch += (this.targetPitch - this.currentPitch) * (1 - DAMPING);
    this.currentFov += (this.targetFov - this.currentFov) * 0.25;
    this.applyToCamera();
  };

  private applyToCamera(): void {
    this.camera.setLocalEulerAngles(this.currentPitch, this.currentYaw, 0);
    if (this.camera.camera) this.camera.camera.fov = this.currentFov;
  }

  private attach(): void {
    this.canvas.addEventListener('pointerdown', this.onPointerDown);
    this.canvas.addEventListener('pointermove', this.onPointerMove);
    this.canvas.addEventListener('pointerup', this.onPointerUp);
    this.canvas.addEventListener('pointercancel', this.onPointerUp);
    this.canvas.addEventListener('pointerleave', this.onPointerUp);
    this.canvas.addEventListener('wheel', this.onWheel, { passive: false });
    this.canvas.addEventListener('contextmenu', this.onContextMenu);
  }

  private detach(): void {
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onPointerUp);
    this.canvas.removeEventListener('pointerleave', this.onPointerUp);
    this.canvas.removeEventListener('wheel', this.onWheel);
    this.canvas.removeEventListener('contextmenu', this.onContextMenu);
  }

  private readonly onPointerDown = (e: PointerEvent): void => {
    if (!this.enabled) return;
    this.canvas.setPointerCapture(e.pointerId);
    this.activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    this.lastPinchDistance = this.computePinchDistance();
  };

  private readonly onPointerMove = (e: PointerEvent): void => {
    if (!this.enabled) return;
    const prev = this.activePointers.get(e.pointerId);
    if (!prev) return;

    const next = { x: e.clientX, y: e.clientY };
    this.activePointers.set(e.pointerId, next);

    if (this.activePointers.size === 1) {
      // Single-pointer drag → yaw/pitch.
      const dx = next.x - prev.x;
      const dy = next.y - prev.y;
      // Yaw uses += (not -=) because panoramaSphere flips emissive UV on X to un-mirror text.
      // With that flip, drag-right should still feel like "look right" — which requires
      // the camera to spin the opposite way around Y. Matches the production Paçoca demo.
      this.targetYaw += dx * DRAG_SENSITIVITY;
      this.targetPitch = clamp(this.targetPitch + dy * DRAG_SENSITIVITY, -PITCH_LIMIT_DEG, PITCH_LIMIT_DEG);
    } else if (this.activePointers.size >= 2) {
      // Two-pointer pinch → FOV.
      const newDist = this.computePinchDistance();
      if (this.lastPinchDistance && newDist) {
        const ratio = this.lastPinchDistance / newDist;
        this.targetFov = clamp(this.targetFov * ratio, FOV_MIN, FOV_MAX);
      }
      this.lastPinchDistance = newDist;
    }
  };

  private readonly onPointerUp = (e: PointerEvent): void => {
    if (this.canvas.hasPointerCapture(e.pointerId)) {
      this.canvas.releasePointerCapture(e.pointerId);
    }
    this.activePointers.delete(e.pointerId);
    if (this.activePointers.size < 2) this.lastPinchDistance = null;
  };

  private readonly onWheel = (e: WheelEvent): void => {
    if (!this.enabled) return;
    e.preventDefault();
    // deltaY > 0 = scroll down = zoom out (wider FOV).
    const next = this.targetFov + Math.sign(e.deltaY) * 3;
    this.targetFov = clamp(next, FOV_MIN, FOV_MAX);
  };

  private readonly onContextMenu = (e: Event): void => e.preventDefault();

  private computePinchDistance(): number | null {
    if (this.activePointers.size < 2) return null;
    const pts = Array.from(this.activePointers.values());
    const a = pts[0];
    const b = pts[1];
    if (!a || !b) return null;
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.hypot(dx, dy);
  }
}

function lerpAngle(current: number, target: number, t: number): number {
  // Take the short way around the circle.
  let delta = (target - current) % 360;
  if (delta > 180) delta -= 360;
  if (delta < -180) delta += 360;
  return current + delta * t;
}
